import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { ToastManager } from '../../ToastManager';
import { IconButton } from '../buttons/IconButton';
import { ListBox } from '../list-box/ListBox';
import { ListBoxSection } from '../list-box/ListBoxSection';
import { Dialog } from '../overlay/Dialog';

import { useAuth } from '../../store/AuthStore';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlowsById } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useAppsById } from '../../store/apps/useApps.ts';
import { useDevicesById } from '../../store/devices/useDevices.ts';
import { useFlowFoldersById } from '../../store/flow-folders/useFlowFolders.ts';
import { useFlowsById } from '../../store/flow/useFlows.ts';
import { useLogsById } from '../../store/insights/useLogs.ts';
import { useApi, useSelectedHomey } from '../../store/SelectedHomeyStore.hooks';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';
import { useZonesById } from '../../store/zones/useZones.ts';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';
import styles from './CommandPaletteDialog.module.css';

import {
  getCommandItems,
  getVisibleCommandItems,
  getCommandSectionLabel,
} from './command-registry.ts';
import { MemoizedCommandPaletteItem } from './CommandPaletteItem';
import { CommandPaletteSearchField } from './CommandPaletteSearchField';
import { useCommandPalette } from './CommandPaletteStore';
import { parseCommandPaletteQuery } from './query-parser';
import { useContextPath } from './useContextPath';
import { useInputPlaceholder } from './useInputPlaceholder';

function selectPaletteState(state) {
  return {
    activeContext: state.activeContext,
    highlightedKey: state.highlightedKey,
    close: state.close,
    setHighlightedKey: state.setHighlightedKey,
    enterScopeContext: state.enterScopeContext,
    enterDeviceContext: state.enterDeviceContext,
    enterFlowContext: state.enterFlowContext,
    enterAppContext: state.enterAppContext,
    enterSettingContext: state.enterSettingContext,
    exitContext: state.exitContext,
  };
}

export function CommandPaletteDialog() {
  const { i18n } = useI18n();
  const { cloudUser, employeeFlag } = useAuth();
  const { api } = useApi();
  const selectedHomey = useSelectedHomey();

  const devices = useDevicesById();
  const flows = useFlowsById();
  const advancedFlows = useAdvancedFlowsById();
  const apps = useAppsById();
  const zones = useZonesById();
  const flowFolders = useFlowFoldersById();
  const logs = useLogsById();
  const { userMe } = useUserMe();
  const { settings: webAppSettings } = useWebAppSettings();

  const {
    activeContext,
    highlightedKey,
    close,
    setHighlightedKey,
    enterScopeContext,
    enterDeviceContext,
    enterFlowContext,
    enterAppContext,
    enterSettingContext,
    exitContext,
  } = useCommandPalette(selectPaletteState, shallow);

  const [inputValue, setInputValue] = useState('');
  const [isRunningCommand, setIsRunningCommand] = useState(false);
  const [contextPrefixWidth, setContextPrefixWidth] = useState(0);

  const inputRef = useRef();
  const prefixRef = useRef();
  const listBoxRef = useRef();
  const listStateRef = useRef({});
  const resultKeysRef = useRef([]);
  const pageStepRef = useRef(10);
  const pendingScrollAlignmentRef = useRef('nearest');

  const parsedQuery = useMemo(() => {
    return parseCommandPaletteQuery(inputValue);
  }, [inputValue]);

  const effectiveQuery = activeContext != null ? inputValue.trim() : parsedQuery.search;

  const deferredQuery = useDeferredValue(effectiveQuery);

  useEffect(() => {
    setInputValue('');
  }, [activeContext]);

  const commandItems = useMemo(() => {
    return getCommandItems({
      query: deferredQuery,
      activeContext,
      i18n,
      api,
      employeeFlag,
      availableHomeys: cloudUser?.homeys ?? [],
      selectedHomey,
      devicesById: devices.byId,
      flowsById: flows.byId,
      advancedFlowsById: advancedFlows.byId,
      appsById: apps.byId,
      zonesById: zones.byId,
      flowFoldersById: flowFolders.byId,
      logsById: logs.byId,
      userMe,
      webAppSettings,
    });
  }, [
    deferredQuery,
    activeContext,
    i18n,
    api,
    employeeFlag,
    cloudUser?.homeys,
    selectedHomey,
    devices.byId,
    flows.byId,
    advancedFlows.byId,
    apps.byId,
    zones.byId,
    flowFolders.byId,
    logs.byId,
    userMe,
    webAppSettings,
  ]);

  const visibleCommands = useMemo(() => {
    return getVisibleCommandItems({
      items: commandItems,
      query: deferredQuery,
      activeContext,
    });
  }, [activeContext, commandItems, deferredQuery]);

  const commandsByKey = useMemo(() => {
    return new Map(
      visibleCommands.map((command) => {
        return [command.key, command];
      }),
    );
  }, [visibleCommands]);

  const sectionedListItems = useMemo(() => {
    const sectionsById = new Map();

    for (const command of visibleCommands) {
      const sectionId = command.section ?? 'command';
      const sectionKey = `section:${sectionId}`;

      if (sectionsById.has(sectionKey) !== true) {
        sectionsById.set(sectionKey, {
          key: sectionKey,
          title: getCommandSectionLabel(sectionId, i18n),
          children: [],
        });
      }

      sectionsById.get(sectionKey).children.push({
        key: command.key,
        textValue: `${command.title} ${command.subtitle ?? ''}`,
        command,
      });
    }

    return [...sectionsById.values()];
  }, [i18n, visibleCommands]);

  const displayOrderedCommands = useMemo(() => {
    return sectionedListItems.flatMap((section) => {
      return section.children.map((item) => {
        return item.command;
      });
    });
  }, [sectionedListItems]);

  const footerCommand = useMemo(() => {
    if (highlightedKey != null && commandsByKey.has(highlightedKey)) {
      return commandsByKey.get(highlightedKey);
    }

    return displayOrderedCommands[0] ?? null;
  }, [commandsByKey, displayOrderedCommands, highlightedKey]);

  const commandIndexByKey = useMemo(() => {
    return new Map(
      displayOrderedCommands.map((command, index) => {
        return [command.key, index];
      }),
    );
  }, [displayOrderedCommands]);

  const contextEntryCandidates = useMemo(() => {
    if (activeContext != null) {
      return [];
    }

    if (parsedQuery.hasContextToken !== true || parsedQuery.search.length === 0) {
      return [];
    }

    return displayOrderedCommands.filter((command) => {
      return (
        command?.meta?.contextType === 'scope' ||
        command?.meta?.contextType === 'setting' ||
        command?.meta?.entityType === 'device' ||
        command?.meta?.entityType === 'flow' ||
        command?.meta?.entityType === 'advancedFlow' ||
        command?.meta?.entityType === 'app'
      );
    });
  }, [activeContext, displayOrderedCommands, parsedQuery.hasContextToken, parsedQuery.search]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const nextResultKeys = displayOrderedCommands.map((command) => {
      return command.key;
    });
    const previousResultKeys = resultKeysRef.current;
    let hasResultChanged = previousResultKeys.length !== nextResultKeys.length;

    if (hasResultChanged !== true) {
      for (let index = 0; index < nextResultKeys.length; index += 1) {
        if (previousResultKeys[index] !== nextResultKeys[index]) {
          hasResultChanged = true;
          break;
        }
      }
    }

    resultKeysRef.current = nextResultKeys;

    if (displayOrderedCommands.length === 0) {
      pendingScrollAlignmentRef.current = 'nearest';
      setHighlightedKey(null);
      return;
    }

    if (hasResultChanged === true) {
      pendingScrollAlignmentRef.current = 'nearest';
      setHighlightedKey(displayOrderedCommands[0].key);
      if (listBoxRef.current != null) {
        listBoxRef.current.scrollTop = 0;
      }
    }
  }, [displayOrderedCommands, setHighlightedKey]);

  const updatePageStep = useEventCallback(() => {
    const listElement = listBoxRef.current;

    if (listElement == null) {
      pageStepRef.current = 10;
      return;
    }

    const optionElement = listElement.querySelector('[data-command-key]');
    const optionHeight = optionElement?.getBoundingClientRect?.().height ?? 0;

    if (optionHeight <= 0) {
      pageStepRef.current = 10;
      return;
    }

    pageStepRef.current = Math.max(1, Math.floor(listElement.clientHeight / optionHeight) - 1);
  });

  useEffect(() => {
    updatePageStep();

    if (typeof ResizeObserver !== 'function') {
      return;
    }

    const listElement = listBoxRef.current;

    if (listElement == null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      updatePageStep();
    });

    observer.observe(listElement);

    const optionElement = listElement.querySelector('[data-command-key]');

    if (optionElement != null) {
      observer.observe(optionElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [displayOrderedCommands, updatePageStep]);

  useEffect(() => {
    if (highlightedKey == null) {
      return;
    }

    const selectionManager = listStateRef.current?.selectionManager;

    if (typeof selectionManager?.setFocusedKey === 'function') {
      selectionManager.setFocusedKey(highlightedKey);
    }

    const listElement = listBoxRef.current;

    if (listElement == null) {
      return;
    }

    const optionElements = listElement.querySelectorAll('[data-command-key]');
    let highlightedElement = null;

    for (const optionElement of optionElements) {
      if (optionElement.getAttribute('data-command-key') === highlightedKey) {
        highlightedElement = optionElement;
        break;
      }
    }

    const scrollAlignment = pendingScrollAlignmentRef.current;
    pendingScrollAlignmentRef.current = 'nearest';

    const sectionRootElement = highlightedElement?.closest('ul')?.parentElement;
    const scrollTargetElement =
      scrollAlignment === 'section-start'
        ? (sectionRootElement ?? highlightedElement)
        : highlightedElement;
    const blockAlignment = scrollAlignment === 'section-start' ? 'start' : 'nearest';

    scrollTargetElement?.scrollIntoView({
      block: blockAlignment,
      inline: 'nearest',
    });
  }, [displayOrderedCommands, highlightedKey]);

  useEffect(() => {
    if (activeContext?.type === 'device') {
      if (devices.byId?.[activeContext.deviceId] == null) {
        exitContext();
      }
      return;
    }

    if (activeContext?.type === 'flow') {
      const flowById = activeContext.isAdvancedFlow === true ? advancedFlows.byId : flows.byId;

      if (flowById?.[activeContext.flowId] == null) {
        exitContext();
      }

      return;
    }

    if (activeContext?.type === 'app') {
      if (apps.byId?.[activeContext.appId] == null) {
        exitContext();
      }

      return;
    }

    if (activeContext?.type === 'setting') {
      if (activeContext.settingId == null || activeContext.settingId === '') {
        exitContext();
      }
    }
  }, [activeContext, advancedFlows.byId, apps.byId, devices.byId, exitContext, flows.byId]);

  const contextPath = useContextPath(activeContext);

  const inputPlaceholder = useInputPlaceholder(activeContext, i18n);

  const contextPrefix = activeContext != null ? `${contextPath} ` : '';

  useEffect(() => {
    if (contextPrefix === '') {
      setContextPrefixWidth(0);
      return;
    }

    const element = prefixRef.current;

    if (element == null) {
      setContextPrefixWidth(0);
      return;
    }

    const update = () => {
      setContextPrefixWidth(element.offsetWidth ?? 0);
    };

    update();

    if (typeof ResizeObserver !== 'function') {
      return;
    }

    const observer = new ResizeObserver(() => {
      update();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [contextPrefix]);

  function getCurrentCommand() {
    const activeElement = document.activeElement;
    const isInputFocused = activeElement === inputRef.current;
    const isListFocused = listBoxRef.current?.contains(activeElement) === true;

    if (isInputFocused !== true && isListFocused !== true) {
      return null;
    }

    if (isInputFocused === true) {
      if (highlightedKey != null && commandsByKey.has(highlightedKey)) {
        return commandsByKey.get(highlightedKey);
      }

      return displayOrderedCommands[0] ?? null;
    }

    const focusedElement =
      activeElement?.getAttribute?.('data-command-key') != null
        ? activeElement
        : activeElement?.closest?.('[data-command-key]');
    const focusedDomKey = focusedElement?.getAttribute?.('data-command-key');

    if (focusedDomKey != null && commandsByKey.has(focusedDomKey)) {
      return commandsByKey.get(focusedDomKey);
    }

    const focusedKey = listStateRef.current?.selectionManager?.focusedKey;

    if (focusedKey != null && commandsByKey.has(focusedKey)) {
      return commandsByKey.get(focusedKey);
    }

    if (highlightedKey != null && commandsByKey.has(highlightedKey)) {
      return commandsByKey.get(highlightedKey);
    }

    return displayOrderedCommands[0] ?? null;
  }

  const contextEntryStrategies = useMemo(() => {
    return [
      {
        getPayload(command) {
          if (command?.meta?.contextType !== 'scope') {
            return null;
          }

          const scopeType = command.meta.scopeType;

          if (
            scopeType !== 'devices' &&
            scopeType !== 'flows' &&
            scopeType !== 'settings' &&
            scopeType !== 'logs'
          ) {
            return null;
          }

          return { scopeType };
        },
        enter(payload) {
          enterScopeContext(payload.scopeType);
          return true;
        },
      },
      {
        getPayload(command) {
          const deviceId = command?.meta?.entityType === 'device' ? command.meta.entityId : null;

          if (deviceId == null) {
            return null;
          }

          const device = devices.byId?.[deviceId];

          if (device == null) {
            return null;
          }

          return { device };
        },
        enter(payload, parentContext) {
          enterDeviceContext(payload.device, { parentContext });
          return true;
        },
      },
      {
        getPayload(command) {
          const flowEntityType = command?.meta?.entityType;

          if (flowEntityType !== 'flow' && flowEntityType !== 'advancedFlow') {
            return null;
          }

          const flowId = command.meta.entityId;
          const isAdvancedFlow = flowEntityType === 'advancedFlow';
          const sourceById = isAdvancedFlow === true ? advancedFlows.byId : flows.byId;
          const flow = sourceById?.[flowId];

          if (flow == null) {
            return null;
          }

          return {
            flow,
            isAdvancedFlow,
          };
        },
        enter(payload, parentContext) {
          enterFlowContext(payload.flow, {
            isAdvancedFlow: payload.isAdvancedFlow,
            parentContext,
          });
          return true;
        },
      },
      {
        getPayload(command) {
          const appId = command?.meta?.entityType === 'app' ? command.meta.entityId : null;

          if (appId == null) {
            return null;
          }

          const app = apps.byId?.[appId] ?? null;

          if (app == null) {
            return null;
          }

          return { app };
        },
        enter(payload, parentContext) {
          enterAppContext(payload.app, { parentContext });
          return true;
        },
      },
      {
        getPayload(command) {
          const settingId =
            command?.meta?.contextType === 'setting' ? command?.meta?.settingId : null;

          if (settingId == null) {
            return null;
          }

          return {
            settingId,
            settingTitle: command?.meta?.settingTitle ?? command?.title ?? settingId,
          };
        },
        enter(payload, parentContext) {
          enterSettingContext(
            {
              settingId: payload.settingId,
              settingTitle: payload.settingTitle,
            },
            { parentContext },
          );
          return true;
        },
      },
    ];
  }, [
    advancedFlows.byId,
    apps.byId,
    devices.byId,
    enterAppContext,
    enterDeviceContext,
    enterFlowContext,
    enterScopeContext,
    enterSettingContext,
    flows.byId,
  ]);

  const tryEnterContextFromCommand = useEventCallback((command) => {
    const parentContext = activeContext?.type === 'scope' ? activeContext : null;

    for (const strategy of contextEntryStrategies) {
      const payload = strategy.getPayload(command);

      if (payload == null) {
        continue;
      }

      const didEnter = strategy.enter(payload, parentContext);

      if (didEnter === true) {
        setInputValue('');
        return true;
      }
    }

    return false;
  });

  useEffect(() => {
    if (contextEntryCandidates.length !== 1) {
      return;
    }

    tryEnterContextFromCommand(contextEntryCandidates[0]);
  }, [contextEntryCandidates, tryEnterContextFromCommand]);

  function getCommandOpenInNewTabPath(command) {
    if (typeof command?.getOpenInNewTabPath !== 'function') {
      return null;
    }

    return command.getOpenInNewTabPath() ?? null;
  }

  const executeCommand = useEventCallback((command, options = {}) => {
    if (command == null || command.isDisabled === true || isRunningCommand === true) {
      return;
    }

    if (activeContext == null && parsedQuery.hasContextToken === true) {
      const entered = tryEnterContextFromCommand(command);

      if (entered === true) {
        return;
      }
    }

    const openInNewTabPath =
      options.openInNewTab === true ? getCommandOpenInNewTabPath(command) : null;

    if (openInNewTabPath != null) {
      window.open(openInNewTabPath, '_blank', 'noopener,noreferrer');
      close();
      return;
    }

    const keepOpenOnShiftEnter =
      command.keepOpenOnShiftEnter === true || command.meta?.keepOpenOnShiftEnter === true;
    const shouldKeepOpen = options.keepOpen === true && keepOpenOnShiftEnter === true;

    setIsRunningCommand(true);

    (async () => {
      await command.onRun?.();

      if (shouldKeepOpen !== true) {
        close();
      }
    })()
      .catch(ToastManager.handleError)
      .finally(() => {
        setIsRunningCommand(false);
      });
  });

  function setHighlightedIndex(index, options = {}) {
    if (displayOrderedCommands.length === 0) {
      return;
    }

    const boundedIndex = Math.max(0, Math.min(index, displayOrderedCommands.length - 1));
    const command = displayOrderedCommands[boundedIndex];

    if (command != null) {
      pendingScrollAlignmentRef.current = getScrollAlignmentForIndex(boundedIndex, options);
      setHighlightedKey(command.key);
    }
  }

  function moveHighlightedIndexWithWrap(direction) {
    if (displayOrderedCommands.length === 0) {
      return;
    }

    const currentIndex = getHighlightedIndex();
    const target = getWrappedArrowTarget(currentIndex, direction);

    if (target == null) {
      return;
    }

    setHighlightedIndex(target.index, {
      direction,
      wrap: target.didWrap,
    });
  }

  function getHighlightedIndex() {
    if (highlightedKey == null) {
      return -1;
    }

    return commandIndexByKey.get(highlightedKey) ?? -1;
  }

  function getScrollAlignmentForIndex(index, options = {}) {
    if (index === 0 && (options.direction === 'up' || options.wrap === true)) {
      return 'section-start';
    }

    return 'nearest';
  }

  function getWrappedArrowTarget(currentIndex, direction) {
    const lastIndex = displayOrderedCommands.length - 1;

    if (lastIndex < 0) {
      return null;
    }

    if (direction === 'down') {
      if (currentIndex < 0) {
        return {
          index: 0,
          didWrap: false,
        };
      }

      if (currentIndex >= lastIndex) {
        return {
          index: 0,
          didWrap: true,
        };
      }

      return {
        index: currentIndex + 1,
        didWrap: false,
      };
    }

    if (direction === 'up') {
      if (currentIndex < 0) {
        return {
          index: lastIndex,
          didWrap: false,
        };
      }

      if (currentIndex <= 0) {
        return {
          index: lastIndex,
          didWrap: true,
        };
      }

      return {
        index: currentIndex - 1,
        didWrap: false,
      };
    }

    return null;
  }

  function getPageStep() {
    return pageStepRef.current;
  }

  function onContextTab(event, command) {
    if (event.shiftKey === true) {
      return false;
    }

    const entered = tryEnterContextFromCommand(command);

    if (entered === true) {
      event.preventDefault();
    }

    return entered;
  }

  function onInputKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        return;
      case 'ArrowDown':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          moveHighlightedIndexWithWrap('down');
        }
        return;
      case 'ArrowUp':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          moveHighlightedIndexWithWrap('up');
        }
        return;
      case 'PageDown':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          const currentIndex = getHighlightedIndex();
          const startIndex = currentIndex < 0 ? 0 : currentIndex;
          setHighlightedIndex(startIndex + getPageStep(), { direction: 'down' });
        }
        return;
      case 'PageUp':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          const currentIndex = getHighlightedIndex();
          const startIndex = currentIndex < 0 ? displayOrderedCommands.length - 1 : currentIndex;
          setHighlightedIndex(startIndex - getPageStep(), { direction: 'up' });
        }
        return;
      case 'Home':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          setHighlightedIndex(0, { direction: 'up' });
        }
        return;
      case 'End':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          setHighlightedIndex(displayOrderedCommands.length - 1, { direction: 'down' });
        }
        return;
      case 'Enter':
        event.preventDefault();
        executeCommand(getCurrentCommand(), {
          keepOpen: event.shiftKey === true && event.metaKey !== true && event.ctrlKey !== true,
          openInNewTab: event.metaKey === true || event.ctrlKey === true,
        });
        return;
      case 'Tab':
        onContextTab(event, getCurrentCommand());
        return;
      case 'Backspace':
        if (activeContext != null && inputValue.length === 0) {
          event.preventDefault();
          exitContext();
        }
        return;
      default:
        return;
    }
  }

  function onResultsKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        return;
      case 'ArrowDown':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          moveHighlightedIndexWithWrap('down');
        }
        return;
      case 'ArrowUp':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          moveHighlightedIndexWithWrap('up');
        }
        return;
      case 'PageDown':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          const currentIndex = getHighlightedIndex();
          const startIndex = currentIndex < 0 ? 0 : currentIndex;
          setHighlightedIndex(startIndex + getPageStep(), { direction: 'down' });
        }
        return;
      case 'PageUp':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          const currentIndex = getHighlightedIndex();
          const startIndex = currentIndex < 0 ? displayOrderedCommands.length - 1 : currentIndex;
          setHighlightedIndex(startIndex - getPageStep(), { direction: 'up' });
        }
        return;
      case 'Home':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          setHighlightedIndex(0, { direction: 'up' });
        }
        return;
      case 'End':
        if (displayOrderedCommands.length > 0) {
          event.preventDefault();
          setHighlightedIndex(displayOrderedCommands.length - 1, { direction: 'down' });
        }
        return;
      case 'Enter':
        event.preventDefault();
        executeCommand(getCurrentCommand(), {
          keepOpen: event.shiftKey === true && event.metaKey !== true && event.ctrlKey !== true,
          openInNewTab: event.metaKey === true || event.ctrlKey === true,
        });
        return;
      case 'Tab':
        onContextTab(event, getCurrentCommand());
        return;
      case 'Backspace':
        event.preventDefault();

        if (activeContext != null && inputValue.length === 0) {
          exitContext();
          inputRef.current?.focus();
          return;
        }

        if (inputValue.length > 0) {
          setInputValue(inputValue.slice(0, -1));
        }

        inputRef.current?.focus();
        return;
      default:
        if (
          event.key.length === 1 &&
          event.metaKey !== true &&
          event.ctrlKey !== true &&
          event.altKey !== true
        ) {
          event.preventDefault();
          setInputValue(`${inputValue}${event.key}`);
          inputRef.current?.focus();
          return;
        }

        return;
    }
  }

  function renderItem({ value }) {
    return (
      <MemoizedCommandPaletteItem
        onPress={executeCommand}
        isHighlighted={highlightedKey === value.command.key}
        onHighlight={setHighlightedKey}
      />
    );
  }

  const disabledCommandKeys = useMemo(() => {
    return visibleCommands
      .filter((command) => {
        return command.isDisabled === true;
      })
      .map((command) => {
        return command.key;
      });
  }, [visibleCommands]);

  return (
    <Dialog
      onClose={close}
      aria-label={i18n.messageFormatter('commandPalette.dialog.title')}
      autoFocus={false}
    >
      <div className={styles.root}>
        <div className={styles.topBar}>
          <CommandPaletteSearchField
            ref={inputRef}
            aria-label={i18n.messageFormatter('commandPalette.dialog.title')}
            placeholder={inputPlaceholder}
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={onInputKeyDown}
            style={{
              '--module-context-prefix-width': `${contextPrefixWidth}px`,
            }}
          >
            {contextPrefix !== '' && (
              <div ref={prefixRef} className={styles.searchPrefix}>
                <bdi>{contextPrefix}</bdi>
              </div>
            )}
          </CommandPaletteSearchField>
          <IconButton
            className={styles.closeButton}
            url={glyph.core.cross}
            color={theme.color.icon_light}
            size={theme.icon.size_small}
            aria-label={i18n.messageFormatter('commandPalette.dialog.closeAriaLabel')}
            onPress={close}
          />
        </div>

        <div className={styles.results}>
          {visibleCommands.length === 0 ? (
            <div className={styles.empty}>{i18n.messageFormatter('common.emptyResults')}</div>
          ) : (
            <ListBox
              className={styles.list}
              aria-label={i18n.messageFormatter('commandPalette.dialog.resultsAriaLabel')}
              items={sectionedListItems}
              disabledKeys={disabledCommandKeys}
              selectionMode="single"
              shouldFocusOnHover={false}
              sectionClassName={styles.sectionRoot}
              sectionHeadingWrapperClassName={styles.sectionHeadingWrapper}
              sectionHeadingClassName={styles.sectionHeading}
              sectionListClassName={styles.sectionList}
              stateRef={listStateRef}
              renderSection={() => {
                return <ListBoxSection />;
              }}
              onSelectionChange={(keys, listState) => {
                const [key] = Array.isArray(keys) ? keys : [];
                const item = key != null ? listState.collection.getItem(key) : null;
                setHighlightedKey(item?.key ?? null);
              }}
              onKeyDown={onResultsKeyDown}
              listBoxRef={listBoxRef}
              renderItem={renderItem}
            />
          )}
        </div>

        <footer className={styles.footer}>
          {/* Keep this focusable so FocusScope does not bounce selection back to the search input. */}
          <div className={styles.shortcutHint} tabIndex={-1}>
            {(() => {
              if (
                footerCommand?.keepOpenOnShiftEnter === true ||
                footerCommand?.meta?.keepOpenOnShiftEnter === true
              ) {
                return (
                  <>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.press')}
                    </span>
                    <kbd className={styles.shortcutHintKey}>Shift + Enter</kbd>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.runKeepOpen')}
                    </span>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.orPress')}
                    </span>
                    <kbd className={styles.shortcutHintKey}>Enter</kbd>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.runAndClose')}
                    </span>
                  </>
                );
              }

              if (footerCommand?.meta?.canEnterContextWithTab === true) {
                return (
                  <>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.press')}
                    </span>
                    <kbd className={styles.shortcutHintKey}>Tab</kbd>
                    <span className={styles.shortcutHintText}>
                      {i18n.messageFormatter('commandPalette.footer.openContext')}
                    </span>
                  </>
                );
              }

              return (
                <>
                  <span className={styles.shortcutHintText}>
                    {i18n.messageFormatter('commandPalette.footer.press')}
                  </span>
                  <kbd className={styles.shortcutHintKey}>Enter</kbd>
                  <span className={styles.shortcutHintText}>
                    {i18n.messageFormatter('commandPalette.footer.runSelectedAction')}
                  </span>
                </>
              );
            })()}
          </div>
        </footer>
      </div>
    </Dialog>
  );
}
